import React, { useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { fmtINR } from "@/lib/format";
import { Sparkles, Zap, CheckCircle2, RotateCw, ArrowRight } from "lucide-react";

/**
 * AI-scored bank reconciliation. Runs against every un-matched bank
 * statement line and proposes the best voucher / invoice / bill with a
 * 0-100 confidence score. Users can bulk-accept high-confidence rows.
 */
const CONFIDENCE_TONE = (c) =>
  c >= 90 ? "bg-emerald-500/15 text-emerald-700 border-emerald-500/40" :
  c >= 75 ? "bg-blue-500/15    text-blue-700    border-blue-500/40"    :
             "bg-amber-500/15   text-amber-700   border-amber-500/40";

export default function BankReconAutoMatch() {
  const [rows, setRows]     = useState([]);
  const [sel, setSel]       = useState(new Set());
  const [minConf, setMinConf] = useState(60);
  const [busy, setBusy]     = useState(false);
  const [applied, setApplied] = useState(0);

  const run = async () => {
    setBusy(true); setRows([]); setSel(new Set()); setApplied(0);
    try {
      const r = await api.post("/bank/auto-match", { min_confidence: minConf });
      setRows(r.data.proposals || []);
      // Auto-select every proposal ≥ 90% confidence
      setSel(new Set(r.data.proposals.filter(p => p.confidence >= 90).map(p => p.entry_id)));
      toast.success(`${r.data.proposals.length} matches found across ${r.data.entries_scanned} bank lines`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Auto-match failed"); }
    setBusy(false);
  };

  const toggle = (id) => {
    const n = new Set(sel);
    n.has(id) ? n.delete(id) : n.add(id);
    setSel(n);
  };
  const toggleAll = () => {
    if (sel.size === rows.length) setSel(new Set());
    else setSel(new Set(rows.map(r => r.entry_id)));
  };

  const apply = async () => {
    if (sel.size === 0) return toast.error("Select at least one proposal");
    setBusy(true);
    try {
      const items = rows.filter(r => sel.has(r.entry_id)).map(r => ({
        entry_id: r.entry_id, candidate_kind: r.candidate_kind, candidate_id: r.candidate_id,
      }));
      const r = await api.post("/bank/auto-match/apply", { items });
      setApplied(r.data.applied);
      toast.success(`Applied ${r.data.applied} matches`);
      // Remove applied rows
      setRows(rows.filter(x => !sel.has(x.entry_id)));
      setSel(new Set());
    } catch (e) { toast.error(e?.response?.data?.detail || "Apply failed"); }
    setBusy(false);
  };

  return (
    <div className="p-6 space-y-5" data-testid="bank-recon-auto">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-accent flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5" /> AI Reconciliation
        </div>
        <h1 className="font-heading text-3xl font-bold mt-1">Bank auto-match</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Score every un-matched bank line against open invoices, bills, and payments. Bulk-accept anything you're confident about.
        </p>
      </div>

      <Card className="p-4 rounded-sm border flex items-end gap-3 flex-wrap">
        <div>
          <Label className="text-[10px] font-mono uppercase text-muted-foreground">Minimum confidence</Label>
          <div className="flex items-center gap-2 mt-1">
            <Input type="range" min={40} max={95} step={5} value={minConf}
                     onChange={e => setMinConf(Number(e.target.value))}
                     className="w-40" data-testid="bank-min-conf" />
            <span className="font-mono text-sm w-10">{minConf}%</span>
          </div>
        </div>
        <Button onClick={run} disabled={busy}
                  className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="bank-run-btn">
          {busy ? <RotateCw className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
          Run auto-match
        </Button>
        {rows.length > 0 && (
          <Button onClick={apply} disabled={busy || sel.size === 0} size="sm"
                    className="rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white gap-1.5 ml-auto" data-testid="bank-apply-btn">
            <CheckCircle2 className="w-4 h-4" /> Approve {sel.size} selected
          </Button>
        )}
      </Card>

      {applied > 0 && (
        <Card className="p-3 rounded-sm bg-emerald-500/5 border-emerald-500/25 text-sm text-emerald-700 flex items-center gap-2" data-testid="bank-applied">
          <CheckCircle2 className="w-4 h-4" /> Marked <b>{applied}</b> bank entries as reconciled.
        </Card>
      )}

      {rows.length > 0 && (
        <Card className="rounded-sm border overflow-hidden">
          <div className="overflow-x-auto max-h-[600px]">
            <table className="w-full text-sm min-w-[1050px]">
              <thead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground bg-muted/40 sticky top-0">
                <tr>
                  <th className="p-2 w-8"><Checkbox checked={sel.size === rows.length} onCheckedChange={toggleAll} data-testid="bank-check-all" /></th>
                  <th className="p-2 text-left">Bank Line</th>
                  <th className="p-2 text-right">Bank ₹</th>
                  <th className="p-2 text-left">Suggested match</th>
                  <th className="p-2 text-right">Doc ₹</th>
                  <th className="p-2 text-left">Confidence</th>
                  <th className="p-2 text-left">Why</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(p => (
                  <tr key={p.entry_id} className={`border-t ${sel.has(p.entry_id) ? "bg-accent/5" : ""}`} data-testid={`bank-row-${p.entry_id}`}>
                    <td className="p-2 text-center">
                      <Checkbox checked={sel.has(p.entry_id)} onCheckedChange={() => toggle(p.entry_id)} data-testid={`bank-check-${p.entry_id}`} />
                    </td>
                    <td className="p-2">
                      <div className="text-[11px] font-mono">{p.entry_date}</div>
                      <div className="text-[11px] text-muted-foreground truncate max-w-[280px]" title={p.entry_narration}>{p.entry_narration}</div>
                    </td>
                    <td className="p-2 text-right font-mono">{fmtINR(p.entry_amount)}</td>
                    <td className="p-2">
                      <div className="text-[11px] font-mono uppercase text-accent">{p.candidate_kind.replace("_", " ")}</div>
                      <div className="text-sm font-medium">{p.candidate_ref}</div>
                      <div className="text-[10px] text-muted-foreground">{p.candidate_party}</div>
                    </td>
                    <td className="p-2 text-right font-mono">{fmtINR(p.candidate_amount)}</td>
                    <td className="p-2">
                      <span className={`px-2 py-0.5 rounded-sm border text-[11px] font-mono ${CONFIDENCE_TONE(p.confidence)}`}
                            data-testid={`bank-conf-${p.entry_id}`}>
                        {p.confidence}%
                      </span>
                    </td>
                    <td className="p-2 text-[10px] text-muted-foreground max-w-[240px]">{p.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {rows.length === 0 && !busy && (
        <Card className="p-10 rounded-sm border text-center text-muted-foreground" data-testid="bank-empty">
          <Sparkles className="w-10 h-10 mx-auto opacity-30" />
          <div className="mt-2 text-sm">Set your confidence floor and click <b>Run auto-match</b> to scan un-matched bank lines.</div>
        </Card>
      )}
    </div>
  );
}
